import React, { useState, useRef, useEffect } from 'react';
import XClose from '../../atoms/Icon/XClose';
import Send from '../../atoms/Icon/Send';

/** Matches portal positioning in AskAsperAccordion. */
export const DISLIKE_FEEDBACK_POPOVER_OUTER_WIDTH = 440;

export interface DislikeFeedbackPopoverProps {
    /** Whether the popover is visible */
    open: boolean;
    /** Callback when the popover should close */
    onClose: () => void;
    /** Callback when feedback is submitted */
    onSubmit: (feedback: string) => void;
    /** Shown as: Describe the issue in "<contextTitle>" */
    contextTitle?: string;
    /** Data test attribute */
    'data-test'?: string;
    /** Data test ID attribute */
    'data-test-id'?: string;
}

/**
 * DislikeFeedbackPopover - A popover component that appears when user clicks the dislike button
 * Allows users to provide additional feedback about why they disliked the content.
 *
 * Design based on Figma: https://www.figma.com/design/tU5vovHzeDFIGuNjQOlBwH/Ask-Asper-3.0?node-id=453-4147
 */
const DislikeFeedbackPopover: React.FC<DislikeFeedbackPopoverProps> = ({
    open,
    onClose,
    onSubmit,
    contextTitle,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const [feedbackText, setFeedbackText] = useState('');
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Focus input when popover opens
    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);

    // Reset text when closed
    useEffect(() => {
        if (!open) {
            setFeedbackText('');
        }
    }, [open]);

    const handleSubmit = () => {
        if (feedbackText.trim()) {
            onSubmit(feedbackText.trim());
            setFeedbackText('');
            onClose();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!open) return null;

    return (
        <div
            className="bg-white border border-solid border-[#e4e4e7] rounded-xl shadow-[2px_4px_4px_0px_rgba(0,0,0,0.1)] px-6 py-4 flex flex-col gap-4 w-[440px] max-w-[min(440px,calc(100vw-24px))] font-['Open_Sans'] box-border"
            data-test={dataTest}
            data-test-id={dataTestId}
            data-dislike-feedback-popover
        >
            <div className="flex items-start justify-between gap-3 w-full">
                {contextTitle ? (
                    <p
                        className="flex-1 text-[14px] leading-[22px] text-[#18181b] m-0 text-left pr-2"
                        style={{ fontWeight: 400 }}
                    >
                        {`Describe the issue in "${contextTitle}"`}
                    </p>
                ) : (
                    <span className="flex-1" />
                )}
                <button
                    type="button"
                    className="flex items-center justify-center size-[22px] rounded cursor-pointer hover:bg-[rgba(0,0,0,0.04)] bg-transparent border-none transition-colors shrink-0 mt-0.5"
                    onClick={onClose}
                    aria-label="Close feedback"
                    data-test-id={`${dataTestId}-close`}
                >
                    <XClose size={16} color="rgba(0,0,0,0.45)" />
                </button>
            </div>

            <textarea
                ref={inputRef}
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Tell us more..."
                rows={5}
                className="w-full font-['Open_Sans'] text-[15px] leading-6 text-[#18181b] placeholder-[#a1a1aa] bg-white border border-solid border-[#e4e4e7] rounded-lg outline-none resize-y min-h-[132px] max-h-[220px] px-3 py-3 box-border"
                data-test-id={`${dataTestId}-input`}
            />

            <div className="flex justify-end w-full">
                <button
                    type="button"
                    className={`flex items-center justify-center size-9 rounded-md p-0 cursor-pointer transition-colors ${
                        feedbackText.trim()
                            ? 'bg-[#1f1f1f] hover:bg-[#333333]'
                            : 'bg-[#1f1f1f] opacity-[0.56] cursor-not-allowed'
                    }`}
                    onClick={handleSubmit}
                    disabled={!feedbackText.trim()}
                    aria-label="Submit feedback"
                    data-test-id={`${dataTestId}-submit`}
                >
                    <Send size={24} color="white" />
                </button>
            </div>
        </div>
    );
};

export default DislikeFeedbackPopover;
